import { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  ChevronRight,
  User,
  Lock,
  Bell,
  ShieldCheck,
  Eye,
  EyeOff,
  X,
  CheckCircle,
  AlertCircle,
  Copy, 
  Building2, 
  CreditCard
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import img from '../../assets/images/profile.png';
import { CiEdit } from "react-icons/ci";
import { apiClient, getDVA, generateDVA } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

const ALLOWED_BANKS = [
  "Wema Bank",
  "Titan Paystack"
];

export default function ProfileDashboard() {
  const { updateUser } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dva, setDva] = useState(null);
  const [dvaLoading, setDvaLoading] = useState(true);
  const [creatingDva, setCreatingDva] = useState(false);

  // PIN States
  const [hasPin, setHasPin] = useState(false);
  const [pinDigits, setPinDigits] = useState(["", "", "", ""]);
  const [confirmPinDigits, setConfirmPinDigits] = useState(["", "", "", ""]);
  const [currentPinDigits, setCurrentPinDigits] = useState(["", "", "", ""]);

  const newPin = pinDigits.join("");
  const confirmPin = confirmPinDigits.join("");
  const currentPin = currentPinDigits.join("");

  // Password States
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [open, setOpen] = useState(null);
  const [notification, setNotification] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  function toggle(section) {
    setOpen(open === section ? null : section);
    if (notification) setNotification(null);
  }

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4500);
  };

  useEffect(() => {
    loadUserProfile();
    loadDVA();
  }, []);

  // Load User Profile
  async function loadUserProfile() {
    try {
      setLoading(true);
      const res = await apiClient.get("/users/me");
      const userData = res.data;
      setUser(userData);
      setFullName(userData.fullName || "");
      setEmail(userData.email || "");
      setPhone(userData.phone || "");
      setHasPin(!!userData.hasPin);

      // Keep AuthContext's user in sync too. useProfileCompletion()
      // reads from AuthContext on other screens (e.g. Add Funds), and
      // without this it only ever reflected whatever was true the last
      // time the person logged in — so a completed profile here would
      // never clear the "complete your profile" prompt over there.
      updateUser({
        fullName: userData.fullName,
        email: userData.email,
        phone: userData.phone,
        hasPin: !!userData.hasPin,
      });
    } catch (err) {
      console.error(err);
      setError("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }

  // Update Profile
  async function handleUpdateProfile() {
    if (!fullName.trim() || !email.trim()) {
      showNotification("error", "Full name and email are required");
      return;
    }
    setUpdating(true);
    try {
      const trimmedFullName = fullName.trim();
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedPhone = phone.trim();

      await apiClient.patch("/users/me", {
        fullName: trimmedFullName,
        email: trimmedEmail,
        phone: trimmedPhone,
      });

      // Update AuthContext immediately, so any screen relying on
      // useProfileCompletion() reflects the change right away instead
      // of waiting on a refetch elsewhere (or a logout/login cycle).
      updateUser({
        fullName: trimmedFullName,
        email: trimmedEmail,
        phone: trimmedPhone,
      });

      showNotification("success", "Profile updated successfully!");
      toggle("profile");
      loadUserProfile();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update profile";
      showNotification("error", msg);
    } finally {
      setUpdating(false);
    }
  }

  // Change Password
  async function handleChangePassword() {
    if (newPassword !== confirmPassword) {
      showNotification("error", "New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      showNotification("error", "New password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/auth/change-password", { currentPassword, newPassword });
      showNotification("success", "Password updated successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toggle("password");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update password";
      showNotification("error", msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Handle PIN Action
  async function handlePinAction() {
    if (newPin !== confirmPin) {
      showNotification("error", "PINs do not match");
      return;
    }
    if (newPin.length !== 4) {
      showNotification("error", "PIN must be 4 digits");
      return;
    }
    if (hasPin && currentPin.length !== 4) {
      showNotification("error", "Please enter your current PIN");
      return;
    }

    setSubmitting(true);
    try {
      if (!hasPin) {
        await apiClient.post("/auth/pin/set", { pin: newPin });
        setHasPin(true);
        updateUser({ hasPin: true });
        showNotification("success", "Transaction PIN created successfully!");
      } else {
        await apiClient.post("/auth/pin/update", { currentPin, newPin });
        showNotification("success", "Transaction PIN updated successfully!");
      }

      // Clear all fields
      setPinDigits(["", "", "", ""]);
      setConfirmPinDigits(["", "", "", ""]);
      setCurrentPinDigits(["", "", "", ""]);
      toggle("pin");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to process PIN";
      showNotification("error", msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function loadDVA() {
    try {
      setDvaLoading(true);

      const res = await getDVA();

      if (res?.data) {
        setDva(res.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setDvaLoading(false);
    }
  }

  async function handleCreateDVA() {
    try {
      setCreatingDva(true);

      const res = await generateDVA("wema-bank");

      if (res?.data) {
        setDva(res.data);
      }

      showNotification(
        "success",
        "Virtual account created successfully"
      );
    } catch (err) {
      showNotification(
        "error",
        err?.response?.data?.message ||
          "Unable to create virtual account"
      );
    } finally {
      setCreatingDva(false);
    }
  }

  function handleLogout() {
    localStorage.clear();
    window.location.href = "/login";
  }

  function copyAccountNumber(number) {
    navigator.clipboard.writeText(number);
    showNotification(
      "success",
      "Account number copied"
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-600">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] pb-24 relative">
      {/* Header */}
      <div className="bg-white px-5 pt-6 pb-5 flex items-center justify-between shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <a href="/">
            <ArrowLeft size={22} className="text-gray-700" />
          </a>
          <h1 className="text-[17px] font-semibold text-gray-800">Profile</h1>
        </div>
        <button
          onClick={handleLogout}
          className="text-sm font-medium text-red-600 hover:text-red-700 transition"
        >
          Logout
        </button>
      </div>

      <div className="px-4 mt-5 space-y-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 relative">
          <CiEdit
            size={20}
            className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 cursor-pointer transition"
            onClick={() => toggle("profile")}
          />
          <div className="flex flex-col items-center">
            <img
              src={img}
              alt="profile"
              className="w-24 h-24 rounded-full object-cover mb-4 border-4 border-gray-100 shadow-sm"
            />
            <h2 className="text-xl font-semibold text-gray-800">{fullName || "User"}</h2>
            <div className="mt-3 text-center text-sm text-gray-600 space-y-1.5">
              <div>{phone || "No phone number set"}</div>
              <div>{email || "No email set"}</div>
            </div>
          </div>
        </div>

        {/* Virtual Account Card */}
        <div className="bg-gradient-to-r from-[#3866A3] to-[#4F82C3] rounded-2xl shadow-sm p-5 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <CreditCard size={22} />
            </div>

            <div>
              <h3 className="font-semibold text-lg">
                Virtual Account
              </h3>
              <p className="text-sm text-white/80">
                Fund your wallet instantly
              </p>
            </div>
          </div>

          {dvaLoading ? (
            <div className="py-4 text-sm">
              Loading account...
            </div>
          ) : dva ? (
            <>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-white/70">
                    Account Number
                  </p>

                  <div className="flex items-center justify-between mt-1">
                    <p className="text-2xl font-bold tracking-wider">
                      {dva.accountNumber}
                    </p>

                    <button
                      onClick={() =>
                        copyAccountNumber(dva.accountNumber)
                      }
                      className="bg-white/20 p-2 rounded-lg"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <p className="text-xs text-white/70">
                      Bank
                    </p>

                    <p className="font-medium">
                      {dva.bankName}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-white/70">
                      Account Name
                    </p>

                    <p className="font-medium truncate">
                      {dva.accountName}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Building2 size={22} />
                <p className="text-sm">
                  No virtual account has been created yet.
                </p>
              </div>

              <button
                onClick={handleCreateDVA}
                disabled={creatingDva}
                className="w-full bg-white text-[#3866A3] font-semibold py-3 rounded-xl"
              >
                {creatingDva
                  ? "Creating Account..."
                  : "Generate Virtual Account"}
              </button>
            </div>
            )}
        </div>

        {/* Settings */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-100">
          <h2 className="px-5 py-4 text-lg font-semibold text-gray-800 border-b">Settings</h2>

          {/* Edit Profile */}
          <SettingsItem
            title="Edit profile"
            icon={<User size={18} />}
            isOpen={open === "profile"}
            onClick={() => toggle("profile")}
          >
            <div className="space-y-5 mt-5 px-3 pb-4">
              <Input label="Full Name" value={fullName} onChange={setFullName} />
              <Input label="Email address" type="email" value={email} onChange={setEmail} />
              <Input label="Phone number" value={phone} onChange={setPhone} />
              <button
                onClick={handleUpdateProfile}
                disabled={updating}
                className="w-full py-3.5 bg-[#3866A3] hover:bg-blue-200 disabled:bg-gray-400 text-white rounded-xl font-medium transition shadow-sm"
              >
                {updating ? "Saving Changes..." : "Save Changes"}
              </button>
            </div>
          </SettingsItem>

          {/* ==================== TRANSACTION PIN SECTION (Auto Advance) ==================== */}
          <SettingsItem
            title="Transaction Pin"
            icon={<ShieldCheck size={18} />}
            isOpen={open === "pin"}
            onClick={() => toggle("pin")}
          >
            <div className="mt-6 px-4 space-y-8 pb-6">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  {hasPin ? "Update Transaction PIN" : "Create Transaction PIN"}
                </p>
              </div>

              {/* Current PIN (Only shown if user already has PIN) */}
              {hasPin && (
                <div>
                  <p className="text-sm text-gray-600 mb-3 text-center">Current PIN</p>
                  <PinInput
                    digits={currentPinDigits}
                    setDigits={setCurrentPinDigits}
                  />
                </div>
              )}

              {/* New PIN */}
              <div>
                <p className="text-sm text-gray-600 mb-3 text-center">New PIN</p>
                <PinInput digits={pinDigits} setDigits={setPinDigits} />
              </div>

              {/* Confirm New PIN */}
              <div>
                <p className="text-sm text-gray-600 mb-3 text-center">Confirm New PIN</p>
                <PinInput
                  digits={confirmPinDigits}
                  setDigits={setConfirmPinDigits}
                />
              </div>

              <button
                onClick={handlePinAction}
                disabled={
                  submitting ||
                  newPin.length !== 4 ||
                  confirmPin.length !== 4 ||
                  (hasPin && currentPin.length !== 4)
                }
                className={`w-full py-3.5 rounded-xl font-medium text-white transition ${
                  newPin.length === 4 &&
                  confirmPin.length === 4 &&
                  (!hasPin || currentPin.length === 4)
                    ? "bg-[#3866A3] hover:bg-blue-200"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                {submitting
                  ? "Processing..."
                  : hasPin
                  ? "Update PIN"
                  : "Create PIN"}
              </button>
            </div>
          </SettingsItem>

          {/* Password settings */}
          <SettingsItem
            title="Password settings"
            icon={<Lock size={18} />}
            isOpen={open === "password"}
            onClick={() => toggle("password")}
          >
            <div className="space-y-5 mt-5 px-3 pb-4">
              <PasswordInput
                label="Current Password"
                value={currentPassword}
                onChange={setCurrentPassword}
              />
              <PasswordInput
                label="New Password"
                value={newPassword}
                onChange={setNewPassword}
              />
              <PasswordInput
                label="Confirm New Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
              />
              <button
                onClick={handleChangePassword}
                disabled={submitting || !currentPassword || !newPassword || !confirmPassword}
                className="w-full py-3.5 bg-[#3866A3] hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-xl font-medium transition shadow-sm"
              >
                {submitting ? "Updating..." : "Update Password"}
              </button>
            </div>
          </SettingsItem>

          {/* Notification settings */}
          <SettingsItem
            title="Notification settings"
            icon={<Bell size={18} />}
            isOpen={open === "notification"}
            onClick={() => toggle("notification")}
          >
            <div className="py-4 px-5">
              <div className="flex items-center justify-between">
                <span className="text-[15px] font-medium text-gray-700 flex items-center gap-3">
                  <Bell size={18} className="text-gray-500" />
                  Push Notifications
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationsEnabled}
                    onChange={() => {
                      const newValue = !notificationsEnabled;
                      setNotificationsEnabled(newValue);
                      showNotification(
                        "success",
                        newValue ? "Push notifications turned on" : "Push notifications turned off"
                      );
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
                </label>
              </div>
            </div>
          </SettingsItem>
        </div>
      </div>

      <MobileBottomNav />

      {/* Notification Popup */}
      <AnimatePresence>
        {notification && (
          <div className="fixed inset-0 z-50 flex items-end justify-center pb-6 px-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.95 }}
              className={`w-full max-w-md pointer-events-auto rounded-2xl shadow-2xl overflow-hidden border ${
                notification.type === "success"
                  ? "bg-green-600 border-green-500"
                  : "bg-red-600 border-red-500"
              }`}
            >
              <div className="flex items-center gap-4 p-5 text-white">
                <div className="flex-shrink-0">
                  {notification.type === "success" ? (
                    <CheckCircle size={28} />
                  ) : (
                    <AlertCircle size={28} />
                  )}
                </div>
                <div className="flex-1 text-[15px] font-medium leading-tight">
                  {notification.message}
                </div>
                <button
                  onClick={() => setNotification(null)}
                  className="flex-shrink-0 opacity-80 hover:opacity-100"
                >
                  <X size={22} />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ====================== Reusable Components ======================

function SettingsItem({ title, icon, isOpen, onClick, children }) {
  return (
    <div>
      <button
        onClick={onClick}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-3.5">
          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600">
            {icon}
          </div>
          <span className="text-[15px] font-medium text-gray-800">{title}</span>
        </div>
        <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronRight size={18} className="text-gray-400" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Input({ label, type = "text", value, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 px-4 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition"
      />
    </div>
  );
}

function PasswordInput({ label, value, onChange }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-12 px-4 pr-12 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none transition"
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
}

// ====================== PIN Input Component with Auto Advance ======================
function PinInput({ digits, setDigits }) {
  const inputsRef = useRef([]);

  const handleChange = (index, value) => {
    const val = value.replace(/\D/, ""); // Only digits

    const newDigits = [...digits];
    newDigits[index] = val;
    setDigits(newDigits);

    // Auto advance to next input
    if (val && index < 3) {
      setTimeout(() => {
        inputsRef.current[index + 1]?.focus();
      }, 10);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index] === "" && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-5">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          type="password"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className="w-14 h-14 bg-gray-100 border border-gray-200 rounded-xl text-center text-2xl font-bold 
                     focus:border-blue-500 focus:bg-white transition outline-none"
          autoComplete="off"
          inputMode="numeric"
        />
      ))}
    </div>
  );
}
